import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { Save, X, Upload, FileAudio, Loader2, CheckCircle, Plus } from 'lucide-react';


// --- Constants ---
const LANGUAGES = [
  { id: 'english', label: 'English' },
  { id: 'hindi', label: 'Hindi' },
  { id: 'transliteration', label: 'Transliteration' },
  { id: 'urdu', label: 'Urdu' },
  { id: 'gujarati', label: 'Gujarati' }
];


const INITIAL_LANG_STATE = {
  title: '', titleAudio: null,
  description: '', descriptionAudio: null,
  tarjama: '', tarjamaAudio: null,
  fazeelat: '', fazeelatAudio: null,
};


// --- Sub-Component: FieldGroup ---
const FieldGroup = React.memo(({
  label,
  textValue,
  onTextChange,
  audioFile,
  onAudioChange,
  inputType = 'text',
  rtl = false,
  fieldName
}) => {
  const [previewUrl, setPreviewUrl] = useState(null);


  useEffect(() => {
    if (!audioFile) {
      setPreviewUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(audioFile);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [audioFile]);


  return (
    <div className="card mb-4 border-light bg-light">
      <div className="card-body">
        <h6 className="card-title text-primary mb-3">{label}</h6>
        <div className="row g-3">
          {/* Text Input Column */}
          <div className="col-lg-7">
            <label className="form-label text-muted small" htmlFor={`text-${fieldName}`}>Text Content</label>
            {inputType === 'textarea' ? (
              <textarea
                id={`text-${fieldName}`}
                className="form-control"
                rows={4}
                value={textValue || ''}
                onChange={(e) => onTextChange(e.target.value)}
                dir={rtl ? 'rtl' : 'ltr'}
                placeholder={`Enter ${label}...`}
              />
            ) : (
              <input
                id={`text-${fieldName}`}
                type={inputType}
                className="form-control"
                value={textValue || ''}
                onChange={(e) => onTextChange(e.target.value)}
                dir={rtl ? 'rtl' : 'ltr'}
                placeholder={`Enter ${label}...`}
              />
            )}
          </div>


          {/* Audio Upload Column */}
          <div className="col-lg-5">
            <label className="form-label text-muted small" htmlFor={`file-${fieldName}`}>Audio File</label>
            <div className="input-group mb-2">
              <input
                id={`file-${fieldName}`}
                type="file"
                className="form-control"
                accept="audio/*"
                onChange={(e) => onAudioChange(e.target.files[0])}
              />
              <span className="input-group-text"><Upload size={16} /></span>
            </div>
           
            {/* Audio Preview Area */}
            {audioFile && (
              <div className="alert alert-success d-flex align-items-center p-2 mb-0 mt-2" style={{backgroundColor: '#e6f4ea', borderColor: '#ceead6'}}>
                <FileAudio size={20} className="me-2 text-success" />
                <div className="flex-grow-1 overflow-hidden">
                  <small className="d-block text-truncate text-success">{audioFile.name}</small>
                </div>
              </div>
            )}
            {previewUrl && (
              <div className="mt-2">
                 <audio controls className="w-100" style={{ height: '32px' }}>
                    <source src={previewUrl} />
                    Your browser does not support the audio element.
                 </audio>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});


// --- Main Component ---
const DuaCreationForm = () => {
  // Modal State
  const [showModal, setShowModal] = useState(false); // Controls the "Add Sabaq" modal


  // Form State
  const [activeTab, setActiveTab] = useState('english');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState(null);


  const [formData, setFormData] = useState({
    orderNumber: '',
    arabicDuaText: '',
    arabicDuaAudio: null,
    arabicSurahText: '',
    arabicSurahAudio: null,
    reference: '',
    languages: {
      english: { ...INITIAL_LANG_STATE },
      hindi: { ...INITIAL_LANG_STATE },
      transliteration: { ...INITIAL_LANG_STATE },
      urdu: { ...INITIAL_LANG_STATE },
      gujarati: { ...INITIAL_LANG_STATE },
    }
  });


  // --- Handlers ---
  const handleShow = () => {
    setShowModal(true);
    setSubmitStatus(null);
  };


  const handleClose = () => {
    if (window.confirm("Are you sure you want to close? Unsaved changes will be lost.")) {
      setShowModal(false);
      resetForm();
    }
  };


  const resetForm = () => {
    setFormData({
        orderNumber: '',
        arabicDuaText: '',
        arabicDuaAudio: null,
        arabicSurahText: '',
        arabicSurahAudio: null,
        reference: '',
        languages: {
          english: { ...INITIAL_LANG_STATE },
          hindi: { ...INITIAL_LANG_STATE },
          transliteration: { ...INITIAL_LANG_STATE },
          urdu: { ...INITIAL_LANG_STATE },
          gujarati: { ...INITIAL_LANG_STATE },
        }
    });
    setErrors({});
    setSubmitStatus(null);
    setActiveTab('english');
  };


  const handleGlobalChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };


  const handleLangChange = (langId, field, value) => {
    setFormData(prev => ({
      ...prev,
      languages: {
        ...prev.languages,
        [langId]: { ...prev.languages[langId], [field]: value }
      }
    }));
  };


  const handleLangFileChange = (langId, field, file) => {
    if (file) {
      setFormData(prev => ({
        ...prev,
        languages: {
          ...prev.languages,
          [langId]: { ...prev.languages[langId], [field]: file }
        }
      }));
    }
  };


  const validateForm = () => {
    const newErrors = {};
    if (!formData.orderNumber) newErrors.orderNumber = "Order Number is required";
    if (!formData.arabicDuaText) newErrors.arabicDuaText = "Arabic Dua Text is required";
   
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);
   
    if (!validateForm()) {
      return;
    }


    setIsSubmitting(true);


    try {
      const apiData = new FormData();
      apiData.append('orderNumber', formData.orderNumber);
      apiData.append('arabicDuaText', formData.arabicDuaText);
      apiData.append('arabicSurahText', formData.arabicSurahText || '');
      apiData.append('reference', formData.reference || '');
     
      if (formData.arabicDuaAudio) apiData.append('arabicDuaAudio', formData.arabicDuaAudio);
      if (formData.arabicSurahAudio) apiData.append('arabicSurahAudio', formData.arabicSurahAudio);


      LANGUAGES.forEach(lang => {
        const langData = formData.languages[lang.id];
        Object.keys(langData).forEach(key => {
          const value = langData[key];
          if (value) {
            apiData.append(`languages[${lang.id}][${key}]`, value);
          }
        });
      });


      console.log("Submitting FormData...", Object.fromEntries(apiData));
      await new Promise(resolve => setTimeout(resolve, 1500));
     
      setSubmitStatus('success');
     
    } catch (error) {
      console.error("Submission Error:", error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className="p-5">
      {/* CSS Injection */}
      <style>{`
        :root {
          --deeniyat-green: #0e5f35;
          --deeniyat-gold: #b89c56;
          --bs-primary: var(--deeniyat-green);
          --bs-primary-rgb: 14, 95, 53;
        }
        .btn-primary { background-color: var(--deeniyat-green); border-color: var(--deeniyat-green); }
        .btn-primary:hover { background-color: #0a4728; border-color: #0a4728; }
        .btn-outline-primary { color: var(--deeniyat-green); border-color: var(--deeniyat-green); }
        .btn-outline-primary:hover { background-color: var(--deeniyat-green); color: white; }
        .text-primary { color: var(--deeniyat-green) !important; }
        .form-control:focus, .form-select:focus { border-color: var(--deeniyat-green); box-shadow: 0 0 0 0.25rem rgba(14, 95, 53, 0.25); }
        .nav-tabs .nav-link { color: #6c757d; border: none; border-bottom: 3px solid transparent; transition: all 0.3s ease; }
        .nav-tabs .nav-link:hover { color: var(--deeniyat-green); border-color: transparent; }
        .nav-tabs .nav-link.active { color: var(--deeniyat-green); font-weight: bold; border-bottom: 3px solid var(--deeniyat-green); background-color: #f8f9fa; }
        .card-header-custom { background-color: white; border-bottom: 2px solid #f0f0f0; }
        .badge-deeniyat { background-color: var(--deeniyat-green); color: white; }
      `}</style>


      {/* --- Trigger Button --- */}
      <div className="text-center mb-5">
          <Button variant="primary" size="lg" onClick={handleShow} className="px-5 py-3 shadow d-flex align-items-center mx-auto">
              <Plus className="me-2" /> Add Sabaq
          </Button>
      </div>


      {/* --- Main Modal --- */}
      <Modal
        show={showModal}
        onHide={() => {}}
        size="xl"
        centered
        backdrop="static"
        keyboard={false}
        scrollable={true}
      >
        <Modal.Header closeButton onHide={handleClose}>
          <div className="d-flex align-items-center">
             <img
              src="https://uat.deeniyat.com/deeniyat/wp-content/uploads/2020/08/Deeniyat_logo@4x.png"
              alt="Deeniyat Logo"
              className="me-3"
              style={{ height: '40px', width: 'auto' }}
            />
            <div>
                <Modal.Title className="fw-bold text-dark h5 mb-0">
                  {/* Since we are focusing on Add, title is fixed. Can be dynamic if edit prop passed */}
                  Create New Dua
                </Modal.Title>
                <small className="text-muted">Hifazat Ki Dua Admin Panel</small>
            </div>
          </div>
        </Modal.Header>
       
        <Modal.Body className="bg-light p-4">
            <Form noValidate onSubmit={handleSubmit}>
               
                {/* Status Messages */}
                {submitStatus === 'success' && (
                <div className="alert alert-success d-flex align-items-center mb-4 shadow-sm" role="alert">
                    <CheckCircle size={24} className="me-2" />
                    <div>Dua saved successfully! Ready for next entry.</div>
                </div>
                )}
                {submitStatus === 'error' && (
                <div className="alert alert-danger mb-4 shadow-sm" role="alert">
                    Something went wrong while saving. Please try again.
                </div>
                )}


                {/* --- 1. GLOBAL SECTION CARD --- */}
                <div className={`card shadow-sm mb-4 border-0 rounded-3 ${errors.orderNumber || errors.arabicDuaText ? 'border border-danger' : ''}`}>
                    <div className="card-header card-header-custom py-3">
                        <h5 className="mb-0 text-dark">
                            <span className="badge badge-deeniyat me-2 rounded-circle">1</span>
                            Global Information
                        </h5>
                    </div>
                    <div className="card-body p-4">
                        <div className="row g-4 mb-4">
                            <div className="col-md-4">
                                <label className="form-label fw-bold">Order Number <span className="text-danger">*</span></label>
                                <input
                                    type="number"
                                    className={`form-control form-control-lg ${errors.orderNumber ? 'is-invalid' : ''}`}
                                    name="orderNumber"
                                    value={formData.orderNumber}
                                    onChange={handleGlobalChange}
                                    placeholder="e.g. 10"
                                />
                                {errors.orderNumber && <div className="invalid-feedback">{errors.orderNumber}</div>}
                            </div>
                            <div className="col-md-8">
                                <label className="form-label fw-bold">Reference (Hawala)</label>
                                <input
                                    type="text"
                                    className="form-control form-control-lg"
                                    name="reference"
                                    value={formData.reference}
                                    onChange={handleGlobalChange}
                                    placeholder="e.g. Bukhari Shareef, Vol 1"
                                />
                            </div>
                        </div>


                        <hr className="my-4 text-muted opacity-25" />


                        {/* Arabic Dua */}
                        <div className="mb-4">
                            {errors.arabicDuaText && <div className="text-danger small mb-1 fw-bold">{errors.arabicDuaText}</div>}
                            <FieldGroup
                                label="Arabic Dua (Main)"
                                fieldName="arabicDua"
                                textValue={formData.arabicDuaText}
                                onTextChange={(val) => {
                                    setFormData(prev => ({ ...prev, arabicDuaText: val }));
                                    if (val && errors.arabicDuaText) setErrors(prev => ({...prev, arabicDuaText: null}));
                                }}
                                audioFile={formData.arabicDuaAudio}
                                onAudioChange={(file) => setFormData(prev => ({ ...prev, arabicDuaAudio: file }))}
                                inputType="textarea"
                                rtl={true}
                            />
                        </div>


                        {/* Arabic Surah */}
                        <FieldGroup
                            label="Arabic Surah / Ayah"
                            fieldName="arabicSurah"
                            textValue={formData.arabicSurahText}
                            onTextChange={(val) => setFormData(prev => ({ ...prev, arabicSurahText: val }))}
                            audioFile={formData.arabicSurahAudio}
                            onAudioChange={(file) => setFormData(prev => ({ ...prev, arabicSurahAudio: file }))}
                            inputType="textarea"
                            rtl={true}
                        />
                    </div>
                </div>


                {/* --- 2. MULTI-LANGUAGE SECTION CARD --- */}
                <div className="card shadow-sm border-0 rounded-3">
                    <div className="card-header card-header-custom py-3">
                        <h5 className="mb-0 text-dark">
                            <span className="badge badge-deeniyat me-2 rounded-circle">2</span>
                            Language Details
                        </h5>
                    </div>
                    <div className="card-body p-0">
                        {/* Tabs Navigation */}
                        <ul className="nav nav-tabs nav-fill bg-light px-3 pt-3 border-bottom-0">
                            {LANGUAGES.map((lang) => (
                                <li className="nav-item" key={lang.id}>
                                    <button
                                        type="button"
                                        className={`nav-link py-3 ${activeTab === lang.id ? 'active' : ''}`}
                                        onClick={() => setActiveTab(lang.id)}
                                    >
                                        {lang.label}
                                    </button>
                                </li>
                            ))}
                        </ul>


                        {/* Tabs Content */}
                        <div className="p-4 border-top">
                            {LANGUAGES.map((lang) => {
                                if (activeTab !== lang.id) return null;
                                const currentData = formData.languages[lang.id];
                               
                                return (
                                    <div key={lang.id} className="fade show active">
                                        <div className="alert border-0 mb-4" style={{ backgroundColor: '#e6f4ea', color: '#0e5f35' }}>
                                            <strong>Editing:</strong> {lang.label} Content
                                        </div>


                                        <FieldGroup
                                            label={`${lang.label} Title`}
                                            fieldName={`${lang.id}-title`}
                                            textValue={currentData.title}
                                            onTextChange={(val) => handleLangChange(lang.id, 'title', val)}
                                            audioFile={currentData.titleAudio}
                                            onAudioChange={(file) => handleLangFileChange(lang.id, 'titleAudio', file)}
                                            inputType="text"
                                        />


                                        <FieldGroup
                                            label={`${lang.label} Description`}
                                            fieldName={`${lang.id}-description`}
                                            textValue={currentData.description}
                                            onTextChange={(val) => handleLangChange(lang.id, 'description', val)}
                                            audioFile={currentData.descriptionAudio}
                                            onAudioChange={(file) => handleLangFileChange(lang.id, 'descriptionAudio', file)}
                                            inputType="text"
                                        />


                                        <FieldGroup
                                            label={`${lang.label} Tarjama (Translation)`}
                                            fieldName={`${lang.id}-tarjama`}
                                            textValue={currentData.tarjama}
                                            onTextChange={(val) => handleLangChange(lang.id, 'tarjama', val)}
                                            audioFile={currentData.tarjamaAudio}
                                            onAudioChange={(file) => handleLangFileChange(lang.id, 'tarjamaAudio', file)}
                                            inputType="textarea"
                                        />


                                        <FieldGroup
                                            label={`${lang.label} Fazeelat (Virtues)`}
                                            fieldName={`${lang.id}-fazeelat`}
                                            textValue={currentData.fazeelat}
                                            onTextChange={(val) => handleLangChange(lang.id, 'fazeelat', val)}
                                            audioFile={currentData.fazeelatAudio}
                                            onAudioChange={(file) => handleLangFileChange(lang.id, 'fazeelatAudio', file)}
                                            inputType="textarea"
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </Form>
        </Modal.Body>
        <Modal.Footer className="bg-white border-top">
            <Button
                variant="outline-secondary"
                onClick={handleClose}
                disabled={isSubmitting}
                className="d-flex align-items-center"
            >
                <X size={18} className="me-2" /> Cancel
            </Button>
            <Button
                variant="primary"
                onClick={handleSubmit} // Trigger form submission
                disabled={isSubmitting}
                className="d-flex align-items-center px-4"
            >
                {isSubmitting ? (
                    <>
                        <Loader2 size={18} className="me-2 animate-spin" /> Saving...
                    </>
                ) : (
                    <>
                        <Save size={18} className="me-2" /> Save Dua
                    </>
                )}
            </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};


export default DuaCreationForm;

